/**
 * Sequence-ID cursor — consumer-side decode + compare.
 *
 * Mirrors `backend/backend-api-service/src/realtime/sequence-id.ts` and the
 * indexer's `sequence_id_bytes` (`backend/indexer-service/src/utils/
 * conversions.rs`). Every NATS event the indexer publishes carries this
 * 15-byte big-endian cursor as the protobuf `sequenceId: bytes` field;
 * every market REST read carries the snapshot high-water in the
 * `lastSeq.sequenceId` envelope as base64 of the same 15 bytes.
 *
 * Layout (15 bytes, big-endian):
 *
 *   [ unused: 4 bits ][ slot: 40 ][ tx_index: 16 ][ cpi_index: 60 ]
 *
 * Big-endian byte order is load-bearing: a plain byte-wise lexicographic
 * comparison has the same order as `(slot, tx_index, cpi_index)` tuple
 * comparison. That lets the realtime apply path drop NATS deltas whose
 * `sequenceId` is `<=` the snapshot watermark, and dedup re-delivered
 * events — see `seq-gate-store.ts`.
 *
 * Platform-agnostic (SDK core, web + RN): no `Buffer`, no `atob` — base64
 * is decoded by the dependency-free decoder below.
 */

export const SEQUENCE_ID_LEN = 15;
const SEQUENCE_SLOT_BITS = 40n;
const SEQUENCE_TX_INDEX_BITS = 16n;
const SEQUENCE_CPI_INDEX_BITS = 60n;
const SEQUENCE_SLOT_SHIFT = SEQUENCE_TX_INDEX_BITS + SEQUENCE_CPI_INDEX_BITS;
const SEQUENCE_TX_INDEX_SHIFT = SEQUENCE_CPI_INDEX_BITS;
const SEQUENCE_SLOT_MAX = 1n << SEQUENCE_SLOT_BITS;
const SEQUENCE_TX_INDEX_MAX = 1n << SEQUENCE_TX_INDEX_BITS;
const SEQUENCE_CPI_INDEX_MAX = 1n << SEQUENCE_CPI_INDEX_BITS;

const B64_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
const B64_LOOKUP: Int8Array = (() => {
  const table = new Int8Array(128).fill(-1);
  for (let i = 0; i < B64_ALPHABET.length; i++) {
    table[B64_ALPHABET.charCodeAt(i)] = i;
  }
  return table;
})();

/**
 * Decode a standard base64 string to bytes. Dependency-free (no `Buffer`,
 * no `atob`) so it is safe in platform-agnostic SDK core. Returns `null`
 * on malformed input — callers treat that as "no cursor".
 */
export function base64ToBytes(b64: string): Uint8Array | null {
  const clean = b64.replace(/=+$/, '');
  const len = clean.length;
  const out = new Uint8Array((len * 3) >> 2);
  let acc = 0;
  let bits = 0;
  let o = 0;
  for (let i = 0; i < len; i++) {
    const code = clean.charCodeAt(i);
    const v = code < 128 ? (B64_LOOKUP[code] ?? -1) : -1;
    if (v < 0) return null;
    acc = (acc << 6) | v;
    bits += 6;
    if (bits >= 8) {
      bits -= 8;
      out[o++] = (acc >> bits) & 0xff;
    }
  }
  return out;
}

/**
 * Decode the snapshot envelope's `lastSeq.sequenceId` (base64 of the
 * 15-byte cursor) to bytes. Returns `null` when the watermark is absent
 * (`sequenceId: null` — indexer has no events yet) or malformed.
 */
export function base64ToCursor(b64: string | null | undefined): Uint8Array | null {
  if (b64 === null || b64 === undefined || b64.length === 0) return null;
  const bytes = base64ToBytes(b64);
  if (bytes === null || !isCanonicalSequenceCursor(bytes)) return null;
  return bytes;
}

/** Encode a canonical cursor for an API snapshot watermark. */
export function cursorToBase64(cursor: Uint8Array): string | null {
  if (!isCanonicalSequenceCursor(cursor)) return null;
  let result = '';
  for (let index = 0; index < cursor.length; index += 3) {
    const first = cursor[index] ?? 0;
    const second = cursor[index + 1];
    const third = cursor[index + 2];
    const packed = (first << 16) | ((second ?? 0) << 8) | (third ?? 0);
    result += B64_ALPHABET[(packed >> 18) & 63] ?? '';
    result += B64_ALPHABET[(packed >> 12) & 63] ?? '';
    result += second === undefined ? '=' : (B64_ALPHABET[(packed >> 6) & 63] ?? '');
    result += third === undefined ? '=' : (B64_ALPHABET[packed & 63] ?? '');
  }
  return result;
}

/** True iff the value is a canonical cursor in the 40/16/60-bit domain. */
export function isCanonicalSequenceCursor(value: unknown): value is Uint8Array {
  return (
    value instanceof Uint8Array &&
    value.length === SEQUENCE_ID_LEN &&
    ((value[0] ?? 0) & 0xf0) === 0
  );
}

/** Pack one accepted chain-event tuple into the canonical 15-byte cursor. */
export function sequenceCursor(slot: bigint, txIndex: number, cpiIndex: bigint): Uint8Array | null {
  if (
    slot < 0n ||
    slot >= SEQUENCE_SLOT_MAX ||
    !Number.isSafeInteger(txIndex) ||
    txIndex < 0 ||
    BigInt(txIndex) >= SEQUENCE_TX_INDEX_MAX ||
    cpiIndex < 0n ||
    cpiIndex >= SEQUENCE_CPI_INDEX_MAX
  ) {
    return null;
  }
  let packed =
    (slot << SEQUENCE_SLOT_SHIFT) | (BigInt(txIndex) << SEQUENCE_TX_INDEX_SHIFT) | cpiIndex;
  const out = new Uint8Array(SEQUENCE_ID_LEN);
  for (let index = SEQUENCE_ID_LEN - 1; index >= 0; index--) {
    out[index] = Number(packed & 0xffn);
    packed >>= 8n;
  }
  return out;
}

/** End-of-slot cursor used to seed a gate from an atomic Solana account snapshot. */
export function endOfSlotSequenceCursor(slot: bigint): Uint8Array | null {
  return sequenceCursor(slot, Number(SEQUENCE_TX_INDEX_MAX - 1n), SEQUENCE_CPI_INDEX_MAX - 1n);
}

/** Recover the 40-bit Solana slot from a validated canonical cursor. */
export function sequenceCursorSlot(cursor: Uint8Array): bigint | null {
  if (!isCanonicalSequenceCursor(cursor)) return null;
  let packed = 0n;
  for (const byte of cursor) {
    packed = (packed << 8n) | BigInt(byte);
  }
  return (packed >> SEQUENCE_SLOT_SHIFT) & (SEQUENCE_SLOT_MAX - 1n);
}

/**
 * Lexicographic byte comparison of two cursors. Because the cursor is a
 * big-endian packing of `(slot, tx_index, cpi_index)`, byte order equals
 * tuple order:
 *
 *   < 0  → `a` is older (lower chain position) than `b`
 *   = 0  → identical
 *   > 0  → `a` is newer than `b`
 *
 * Length-mismatched inputs compare by common prefix, then by length —
 * defensive only; both sides are always `SEQUENCE_ID_LEN`.
 */
export function compareCursor(a: Uint8Array, b: Uint8Array): number {
  const n = Math.min(a.length, b.length);
  for (let i = 0; i < n; i++) {
    const av = a[i] as number;
    const bv = b[i] as number;
    if (av !== bv) return av < bv ? -1 : 1;
  }
  if (a.length === b.length) return 0;
  return a.length < b.length ? -1 : 1;
}

/** True iff `a` is strictly newer than `b`. */
export function isCursorNewer(a: Uint8Array, b: Uint8Array): boolean {
  return compareCursor(a, b) > 0;
}

/**
 * Stable hex string key for a cursor — useful for `Set`/`Map` keys and
 * for devtool tracing.
 */
export function cursorKey(cursor: Uint8Array): string {
  let s = '';
  for (let i = 0; i < cursor.length; i++) {
    s += (cursor[i] as number).toString(16).padStart(2, '0');
  }
  return s;
}

/** Read a base-128 varint at `encoded[start]`; returns value + next index,
 *  or `null` on overrun / over-long encoding. Internal to the extractor. */
function readVarint(encoded: Uint8Array, start: number): { value: number; next: number } | null {
  let value = 0;
  let shift = 0;
  let i = start;
  while (i < encoded.length) {
    const b = encoded[i++] as number;
    value |= (b & 0x7f) << shift;
    if ((b & 0x80) === 0) return { value: value >>> 0, next: i };
    shift += 7;
    if (shift > 28) return null;
  }
  return null;
}

/**
 * Extract the `sequenceId` (protobuf field 1, `bytes`) from a raw encoded
 * Morfi event WITHOUT the per-event schema.
 *
 * Every event message the indexer publishes declares `bytes sequence_id =
 * 1`. This scans the protobuf wire format for field 1 / wiretype LEN and
 * returns its bytes — schema-free, so the realtime dispatch chokepoint can
 * gate any event type uniformly without knowing which proto it is. Field
 * order on the wire is not assumed; all fields are walked. Returns `null`
 * if field 1 is absent or the buffer is malformed.
 */
export function extractSequenceId(encoded: Uint8Array): Uint8Array | null {
  let i = 0;
  const n = encoded.length;
  while (i < n) {
    const tag = readVarint(encoded, i);
    if (tag === null) return null;
    i = tag.next;
    const fieldNo = tag.value >>> 3;
    const wireType = tag.value & 0x7;

    if (wireType === 2) {
      const len = readVarint(encoded, i);
      if (len === null || i + len.value > n) return null;
      i = len.next;
      if (fieldNo === 1) {
        const cursor = encoded.subarray(i, i + len.value);
        return isCanonicalSequenceCursor(cursor) ? cursor : null;
      }
      i += len.value;
    } else if (wireType === 0) {
      const v = readVarint(encoded, i);
      if (v === null) return null;
      i = v.next;
    } else if (wireType === 1) {
      i += 8;
    } else if (wireType === 5) {
      i += 4;
    } else {
      // Groups (3/4) / unknown — Morfi event protos use none of these.
      return null;
    }
  }
  return null;
}
